import { useState } from 'react';

const Donate = () => {
  const [donationAmount, setDonationAmount] = useState('');
  const [donationType, setDonationType] = useState('one-time');
  const [selectedAmount, setSelectedAmount] = useState(null);

  const predefinedAmounts = [25, 50, 100, 250, 500, 1000];

  const donationImpacts = [
    {
      amount: '$25',
      impact: 'Provides ballet shoes for one student',
      icon: '🩰'
    },
    {
      amount: '$50',
      impact: 'Sponsors one week of classes for a scholarship student',
      icon: '📚'
    },
    {
      amount: '$100',
      impact: 'Covers costume materials for one dancer',
      icon: '👗'
    },
    {
      amount: '$250',
      impact: 'Supports one month of professional instruction',
      icon: '🎭'
    },
    {
      amount: '$500',
      impact: 'Funds guest artist workshop for all students',
      icon: '⭐'
    },
    {
      amount: '$1000',
      impact: 'Sponsors a full scholarship for one student',
      icon: '🎓'
    }
  ];

  const supportPrograms = [
    {
      title: 'Scholarship Fund',
      description: 'Help talented students access quality ballet training regardless of financial circumstances.',
      goal: '$15,000',
      raised: '$8,500',
      percentage: 57
    },
    {
      title: 'Performance Fund',
      description: 'Support our professional productions and bring world-class ballet to our community.',
      goal: '$25,000',
      raised: '$18,200',
      percentage: 73
    },
    {
      title: 'Facility Enhancement',
      description: 'Improve our studios and performance spaces for the best possible training environment.',
      goal: '$10,000',
      raised: '$3,800',
      percentage: 38
    }
  ];

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setDonationAmount(amount.toString());
  };

  const handleCustomAmount = (e) => {
    setDonationAmount(e.target.value);
    setSelectedAmount(null);
  };

  return (
    <main className="min-h-screen bg-ballet-light">
      {/* Hero Section */}
      <div className="bg-elegant-gradient text-white py-20">
        <div className="container-custom text-center">
          <h1 className="text-5xl lg:text-6xl font-elegant font-bold mb-6">
            Support Northwest Ballet
          </h1>
          <p className="text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto mb-8">
            Your donation helps us continue providing exceptional ballet education and performances 
            to our community
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="btn-primary bg-white text-ballet-burgundy hover:bg-ballet-light">
              Donate Now
            </button>
            <button className="btn-outline border-white text-white hover:bg-white hover:text-ballet-burgundy">
              Learn More
            </button>
          </div>
        </div>
      </div>

      <div className="container-custom section-padding">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Donation Form */}
          <div className="card-elevated">
            <div className="p-8">
              <h2 className="text-3xl font-elegant font-bold gradient-text mb-8 text-center">
                Make a Donation
              </h2>

              {/* Donation Type */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-ballet-burgundy mb-4">Donation Type</h3>
                <div className="flex gap-4">
                  <button
                    onClick={() => setDonationType('one-time')}
                    className={`flex-1 py-3 px-6 rounded-xl font-semibold transition-all duration-300 ${
                      donationType === 'one-time'
                        ? 'bg-ballet-burgundy text-white shadow-lg'
                        : 'bg-ballet-light text-ballet-burgundy hover:bg-ballet-burgundy hover:text-white'
                    }`}
                  >
                    One-Time
                  </button>
                  <button
                    onClick={() => setDonationType('monthly')}
                    className={`flex-1 py-3 px-6 rounded-xl font-semibold transition-all duration-300 ${
                      donationType === 'monthly'
                        ? 'bg-ballet-burgundy text-white shadow-lg'
                        : 'bg-ballet-light text-ballet-burgundy hover:bg-ballet-burgundy hover:text-white'
                    }`}
                  >
                    Monthly
                  </button>
                </div>
              </div>

              {/* Amount Selection */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-ballet-burgundy mb-4">Select Amount</h3>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {predefinedAmounts.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => handleAmountSelect(amount)}
                      className={`py-3 px-4 rounded-xl font-semibold transition-all duration-300 ${
                        selectedAmount === amount
                          ? 'bg-ballet-burgundy text-white shadow-lg'
                          : 'bg-ballet-light text-ballet-burgundy hover:bg-ballet-burgundy hover:text-white'
                      }`}
                    >
                      ${amount}
                    </button>
                  ))}
                </div>
                
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg">$</span>
                  <input
                    type="number"
                    value={donationAmount}
                    onChange={handleCustomAmount}
                    placeholder="Enter custom amount"
                    className="form-input pl-8 text-lg"
                    min="1"
                  />
                </div>
              </div>

              {/* Donation Impact */}
              {donationAmount && (
                <div className="mb-8 p-4 bg-soft-gradient rounded-xl">
                  <h4 className="font-semibold text-ballet-burgundy mb-2">Your Impact</h4>
                  <p className="text-gray-700">
                    Your ${donationAmount} donation will help support our mission to provide 
                    exceptional ballet education and performances to our community.
                  </p>
                </div>
              )}

              <button className="w-full btn-primary text-lg py-4">
                {donationType === 'monthly' ? 'Start Monthly Donation' : 'Donate Now'}
              </button>

              <div className="mt-6 text-center text-sm text-gray-600">
                <p>Your donation is tax-deductible. You will receive a receipt.</p>
                <p className="mt-2">Northwest Ballet is a 501(c)(3) nonprofit organization.</p>
              </div>
            </div>
          </div>

          {/* Impact Information */}
          <div className="space-y-8">
            {/* Donation Impact Examples */}
            <div className="card-elevated">
              <div className="p-8">
                <h3 className="text-2xl font-elegant font-bold text-ballet-burgundy mb-6 text-center">
                  Your Donation Impact
                </h3>
                <div className="space-y-4">
                  {donationImpacts.map((impact, index) => (
                    <div key={index} className="flex items-center p-4 bg-ballet-light rounded-xl">
                      <div className="text-3xl mr-4">{impact.icon}</div>
                      <div>
                        <div className="font-semibold text-ballet-burgundy">{impact.amount}</div>
                        <div className="text-gray-700">{impact.impact}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Current Fundraising Goals */}
            <div className="card-elevated">
              <div className="p-8">
                <h3 className="text-2xl font-elegant font-bold text-ballet-burgundy mb-6 text-center">
                  Current Fundraising Goals
                </h3>
                <div className="space-y-6">
                  {supportPrograms.map((program, index) => (
                    <div key={index} className="bg-ballet-light p-6 rounded-xl">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-semibold text-ballet-burgundy">{program.title}</h4>
                        <span className="text-sm text-gray-600">{program.raised} / {program.goal}</span>
                      </div>
                      <p className="text-gray-700 text-sm mb-3">{program.description}</p>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="bg-ballet-burgundy h-3 rounded-full transition-all duration-500"
                          style={{ width: `${program.percentage}%` }}
                        ></div>
                      </div>
                      <div className="text-right text-sm text-gray-600 mt-1">
                        {program.percentage}% funded
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Other Ways to Support */}
            <div className="card-elevated">
              <div className="p-8">
                <h3 className="text-2xl font-elegant font-bold text-ballet-burgundy mb-6 text-center">
                  Other Ways to Support
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center p-4 bg-ballet-light rounded-xl">
                    <div className="w-12 h-12 bg-ballet-burgundy rounded-xl flex items-center justify-center mr-4">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-ballet-burgundy">Volunteer</h4>
                      <p className="text-gray-700 text-sm">Help with events, performances, and daily operations</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center p-4 bg-ballet-light rounded-xl">
                    <div className="w-12 h-12 bg-ballet-burgundy rounded-xl flex items-center justify-center mr-4">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-ballet-burgundy">Corporate Sponsorship</h4>
                      <p className="text-gray-700 text-sm">Partner with us for performances and events</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center p-4 bg-ballet-light rounded-xl">
                    <div className="w-12 h-12 bg-ballet-burgundy rounded-xl flex items-center justify-center mr-4">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-ballet-burgundy">Legacy Giving</h4>
                      <p className="text-gray-700 text-sm">Include Northwest Ballet in your estate planning</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Donate;
